import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Navigation } from './Navigation';
import CookieConsent from '../CookieConsent';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { X, Mail, Loader2 } from 'lucide-react';

const FOOTERLESS_PATHS = ['/messages'];

interface PlatformLayoutProps {
  children: React.ReactNode;
}

const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    await supabase.auth.resend({ type: 'signup', email: user.email });
    setResending(false);
    setSent(true);
  };

  return (
    <div className="bg-yellow-900/40 border-b border-yellow-800/50 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={14} className="text-yellow-400 flex-shrink-0" />
          <p className="text-xs text-yellow-200 truncate">
            {sent ? 'Verification email sent! Check your inbox.' : 'Please verify your email to unlock all features.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!sent && (
            <button onClick={handleResend} disabled={resending} className="text-xs font-medium text-yellow-300 hover:text-yellow-100 transition-colors whitespace-nowrap">
              {resending ? <Loader2 size={12} className="animate-spin" /> : 'Resend'}
            </button>
          )}
          <button onClick={() => setDismissed(true)} className="p-0.5 text-yellow-500 hover:text-yellow-300">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlatformLayout: React.FC<PlatformLayoutProps> = ({ children }) => {
  const { pathname } = useLocation();
  const hideFooter = FOOTERLESS_PATHS.some(p => pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-district-black overflow-x-hidden w-full max-w-[100vw]">
      {/* Grain Overlay */}
      <div className="grain-overlay" />

      {/* Navigation */}
      <Navigation />

      {/* Email Verification Banner */}
      <div className="pt-16 lg:pt-20">
        <EmailVerificationBanner />
      </div>

      {/* Main Content */}
      <main className="overflow-x-hidden">
        {children}
      </main>

      {/* Footer */}
      {!hideFooter && <footer className="border-t border-gray-800 mt-12 sm:mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-xl sm:text-2xl font-bold tracking-tighter mb-3 sm:mb-4">
                DISTRICT<span className="text-district-red">88</span>
              </h3>
              <p className="text-sm text-gray-100 leading-relaxed">
                A digital district where culture, commerce, and community exist together.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-xs uppercase tracking-ultra text-gray-200 mb-4">Navigate</h4>
              <ul className="space-y-2">
                <li><a href="/market" className="text-sm text-gray-100 hover:text-district-white transition-colors">Market</a></li>
                <li><a href="/culture" className="text-sm text-gray-100 hover:text-district-white transition-colors">Culture</a></li>
                <li><a href="/learn" className="text-sm text-gray-100 hover:text-district-white transition-colors">Learn</a></li>
                <li><a href="/community" className="text-sm text-gray-100 hover:text-district-white transition-colors">Community</a></li>
              </ul>
            </div>

            {/* For You */}
            <div>
              <h4 className="text-xs uppercase tracking-ultra text-gray-200 mb-4">For You</h4>
              <ul className="space-y-2">
                <li><a href="/sell" className="text-sm text-gray-100 hover:text-district-white transition-colors">Start Selling</a></li>
                <li><a href="/creators" className="text-sm text-gray-100 hover:text-district-white transition-colors">Become a Creator</a></li>
                <li><a href="/sole-mates" className="text-sm text-gray-100 hover:text-district-white transition-colors">Sole Mates</a></li>
                <li><a href="/about" className="text-sm text-gray-100 hover:text-district-white transition-colors">The District</a></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="text-xs uppercase tracking-ultra text-gray-200 mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-100 hover:text-district-white transition-colors">Instagram</a></li>
                <li><a href="#" className="text-sm text-gray-100 hover:text-district-white transition-colors">Twitter</a></li>
                <li><a href="#" className="text-sm text-gray-100 hover:text-district-white transition-colors">Discord</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-8 sm:mt-12 lg:mt-16 pt-6 sm:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-gray-200 tracking-wide">
              © 2026 DISTRICT88. THIS IS FOR THE DISTRICT.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="/privacy" className="text-xs text-gray-200 hover:text-district-white transition-colors">Privacy</a>
              <a href="/terms" className="text-xs text-gray-200 hover:text-district-white transition-colors">Terms</a>
              <a href="/refunds" className="text-xs text-gray-200 hover:text-district-white transition-colors">Refunds</a>
              <a href="mailto:info@shopdistrict88.com" className="text-xs text-gray-200 hover:text-district-white transition-colors">Help</a>
            </div>
          </div>
        </div>
      </footer>}

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
};

export default PlatformLayout;
